import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { Reveal, SectionLabel } from "./Placeholder";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "", email: "", company: "", phone: "",
  structure_type: "", units: "", message: "",
  consent: false, website: "", // website = honeypot
};

export default function ContactForm() {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSending(true);
    try {
      // consent implicit: privacy policy notice shown near submit
      await axios.post(`${API}/demo-request`, { ...form, consent: true });
      toast.success(t.form.success);
      setDone(true);
      setForm(initial);
    } catch (err) {
      toast.error(t.form.error);
    } finally {
      setSending(false);
    }
  };

  const field = "w-full rounded-md border border-border bg-white px-4 py-3 font-body text-base text-ink outline-none transition-colors placeholder:text-ink/40 focus:border-electric-blue focus:ring-2 focus:ring-electric-blue/20";

  return (
    <section id="contact" className="bg-secondary py-24 sm:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionLabel>{t.nav.contacts}</SectionLabel>
            <h2 className="font-sans text-4xl font-extrabold leading-tight tracking-tight text-ink sm:text-5xl lg:text-6xl">
              {t.form.title}
            </h2>
            <p className="mt-6 max-w-md font-body text-lg leading-relaxed text-ink/70">{t.form.sub}</p>

            <div className="mt-10 space-y-6 font-body text-ink/80">
              <div>
                <span className="block font-mono text-[10px] uppercase tracking-widest text-ink/50">{t.contactInfo.general}</span>
                <a href="mailto:info@boxlyitalia.it" className="mt-1 flex items-center gap-3 text-base transition-colors hover:text-electric-blue" data-testid="contact-general-email">
                  <CheckCircle2 className="h-5 w-5 text-electric-blue" />
                  info@boxlyitalia.it
                </a>
              </div>
              <div>
                <span className="block font-mono text-[10px] uppercase tracking-widest text-ink/50">{t.contactInfo.commercial}</span>
                <a href="mailto:emanuele@boxlyitalia.it" className="mt-1 flex items-center gap-3 text-base transition-colors hover:text-electric-blue" data-testid="contact-commercial-email">
                  <CheckCircle2 className="h-5 w-5 text-electric-blue" />
                  emanuele@boxlyitalia.it
                </a>
              </div>
              <div className="flex items-center gap-3 text-base">
                <CheckCircle2 className="h-5 w-5 text-electric-blue" />
                {t.contactInfo.location}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="rounded-lg border border-border bg-white p-6 sm:p-8" data-testid="demo-form" noValidate>
              {/* honeypot */}
              <input type="text" name="website" tabIndex="-1" autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="f-name" className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-ink/60">{t.form.name} *</label>
                  <input id="f-name" className={field} value={form.name} onChange={set("name")} required data-testid="form-name" />
                </div>
                <div>
                  <label htmlFor="f-email" className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-ink/60">{t.form.email} *</label>
                  <input id="f-email" type="email" className={field} value={form.email} onChange={set("email")} required data-testid="form-email" />
                </div>
                <div>
                  <label htmlFor="f-company" className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-ink/60">{t.form.company}</label>
                  <input id="f-company" className={field} value={form.company} onChange={set("company")} data-testid="form-company" />
                </div>
                <div>
                  <label htmlFor="f-phone" className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-ink/60">{t.form.phone}</label>
                  <input id="f-phone" className={field} value={form.phone} onChange={set("phone")} data-testid="form-phone" />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="f-structure" className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-ink/60">{t.form.structure}</label>
                  <select id="f-structure" className={field} value={form.structure_type} onChange={set("structure_type")} data-testid="form-structure">
                    <option value="">{t.form.structurePlaceholder}</option>
                    {t.form.structures.map((s, i) => <option key={i} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="f-units" className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-ink/60">{t.form.units}</label>
                  <input id="f-units" className={field} value={form.units} onChange={set("units")} data-testid="form-units" />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="f-message" className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-ink/60">{t.form.message}</label>
                <textarea id="f-message" className={`${field} min-h-[120px] resize-y`} value={form.message} onChange={set("message")} data-testid="form-message" />
              </div>

              <button type="submit" disabled={sending} className="group mt-6 flex w-full items-center justify-center gap-2 rounded-md bg-electric-blue px-7 py-4 font-grotesk text-base font-bold text-white transition-colors duration-300 hover:bg-ink hover:text-lime-accent disabled:opacity-60 sm:w-auto" data-testid="form-submit">
                {sending ? t.form.sending : t.form.submit}
                {!sending && <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
              </button>

              <p className="mt-4 font-body text-xs leading-relaxed text-ink/60" data-testid="form-privacy-notice">
                {t.form.consent}{" "}
                <Link to="/privacy-policy" className="text-electric-blue underline underline-offset-2" data-testid="form-privacy-link">
                  {t.form.consentLink}
                </Link>{" "}
                {t.form.consentEnd}
              </p>

              {done && (
                <p className="mt-4 flex items-center gap-2 font-body text-sm text-electric-blue" data-testid="form-success-msg">
                  <CheckCircle2 className="h-4 w-4" /> {t.form.success}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
